import { ChevronDown } from "@untitledui/icons";
import {
  Button as AriaButton,
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  Popover as AriaPopover,
  Select as AriaSelect,
} from "react-aria-components";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import { CreatorTag } from "@/pages/creator-tags/tag";
import { cx } from "@/utils/cx";

interface CohortTargetSelectProps {
  value: string;
  onChange: (cohortId: string) => void;
  isDisabled?: boolean;
  isInvalid?: boolean;
}

export function CohortTargetSelect({ value, onChange, isDisabled, isInvalid }: CohortTargetSelectProps) {
  const { data: cohorts = [] } = useCreatorCohorts();
  const selected = cohorts.find((cohort) => cohort.id === value);

  return (
    <div className="flex flex-row items-center gap-2">
      <span className="text-sm text-secondary">Assign to cohort:</span>
      <AriaSelect
        aria-label="Target cohort"
        selectedKey={value || null}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        onSelectionChange={(key) => {
          if (typeof key === "string") onChange(key);
        }}
      >
        <AriaButton
          className={cx(
            "flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-2 outline-hidden transition-colors",
            isInvalid ? "border-error" : "border-secondary",
            isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-primary",
          )}
        >
          {selected ? (
            <CreatorTag tag={selected} showTooltip={false} />
          ) : (
            <span className="px-1.5 text-sm text-placeholder">Select a cohort</span>
          )}
          <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-quaternary" />
        </AriaButton>

        <AriaPopover
          offset={4}
          className="min-w-[12rem] overflow-auto rounded-lg bg-primary py-1 shadow-lg ring-1 ring-secondary_alt outline-hidden"
        >
          <AriaListBox className="outline-hidden">
            {cohorts.map((cohort) => (
              <AriaListBoxItem
                key={cohort.id}
                id={cohort.id}
                textValue={cohort.name}
                className={({ isFocused }) =>
                  cx("flex cursor-pointer items-center px-3 py-2 outline-hidden", isFocused && "bg-primary_hover")
                }
              >
                <CreatorTag tag={cohort} showTooltip={false} />
              </AriaListBoxItem>
            ))}
          </AriaListBox>
        </AriaPopover>
      </AriaSelect>
    </div>
  );
}
